import React, { useContext, useEffect, useState, useRef } from "react";
import ContentLayout from "../../component/ContentLayout";
import axios from "../../util/axios";
import { Context } from "../../component/Context/ErrorContext";
import moment from "moment/moment";
import CustomCard from "../../component/card";
import { FaVideo } from "react-icons/fa";

interface Video {
  url: string;
  created_at: string;
  user: {
    first_name: string;
  };
}

interface VideoResponse {
  status: boolean;
  message: string;
  data: {
    videos: Video[];
    total: number;
    page: number;
    totalPages: number;
  };
}

const Videos = () => {
  const { setError } = useContext(Context);
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState<Video[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const fetchVideos = async (currentPage = 1) => {
    try {
      setLoading(true);
      const result = await axios({
        URL: `/omw/subadmin-getVideoList?page=${currentPage}&limit=${pageSize}`,
        method: "GET",
      });
      if (result?.data?.data) {
        setVideos(result.data.data.videos || []);
        setTotal(result.data.data.total || 0);
        setTotalPages(result.data.data.totalPages || 0);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to fetch videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos(page);
  }, [page]);

  useEffect(() => {
    if (selectedVideo && videoRef.current) {
      videoRef.current.load();
    }
  }, [selectedVideo]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <ContentLayout
      className={"items-start rounded-3xl m-2 gap-4 overflow-y-scroll"}
    >
      <p className={"text-3xl font-bold text-primaryText"}>Videos</p>

      {loading && videos.length === 0 ? (
        <div className="w-full flex justify-center items-center py-20">
          <p className="text-primaryText text-lg">Loading videos...</p>
        </div>
      ) : videos.length === 0 ? (
        <div className="w-full flex justify-center items-center py-20">
          <p className="text-primaryText text-lg">No videos found</p>
        </div>
      ) : (
        <>
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((video, index) => (
              <div
                key={index}
                className="cursor-pointer"
                onClick={() => setSelectedVideo(video)}
              >
                <CustomCard
                  mainClass={"bg-primary flex-col gap-3 hover:bg-gray transition-colors"}
                >
                  <div className="w-full aspect-video bg-gray rounded-lg overflow-hidden relative group">
                    <video
                      src={video.url}
                      className="w-full h-full object-cover"
                      controls={false}
                      preload="metadata"
                    />
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <FaVideo className="text-white text-4xl opacity-70" />
                    </div>
                  </div>
                  <div className="w-full flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <p className="text-primaryText text-sm font-semibold">
                        {video.user?.first_name || "Unknown User"}
                      </p>
                      <p className="text-grayLight text-xs">
                        {moment(video.created_at).format("MM/DD/YYYY")}
                      </p>
                    </div>
                    <p className="text-grayLight text-xs">
                      {moment(video.created_at).format("hh:mm A")}
                    </p>
                  </div>
                </CustomCard>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="w-full flex justify-center items-center gap-2 py-4">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  page === 1
                    ? "bg-gray text-grayLight cursor-not-allowed"
                    : "bg-sidebarSelectText text-primaryText hover:bg-blue-600"
                }`}
              >
                Previous
              </button>
              <span className="text-primaryText font-semibold">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  page === totalPages
                    ? "bg-gray text-grayLight cursor-not-allowed"
                    : "bg-sidebarSelectText text-primaryText hover:bg-blue-600"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="bg-primary rounded-xl p-4 max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-primaryText text-lg font-semibold">
                  {selectedVideo.user?.first_name || "Unknown User"}
                </p>
                <p className="text-grayLight text-sm">
                  {moment(selectedVideo.created_at).format("MM/DD/YYYY hh:mm A")}
                </p>
              </div>
              <button
                onClick={() => setSelectedVideo(null)}
                className="text-primaryText hover:text-sidebarSelectText text-2xl font-bold"
                aria-label="Close video"
              >
                ×
              </button>
            </div>
            <div className="w-full bg-black rounded-lg overflow-hidden relative" style={{ aspectRatio: '16/9', minHeight: '400px', position: 'relative' }}>
              <video
                ref={videoRef}
                src={selectedVideo.url}
                controls
                autoPlay
                playsInline
                preload="auto"
                style={{ 
                  display: 'block', 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#000'
                }}
                onError={(e) => {
                  console.error('Video error:', e);
                  const video = e.currentTarget;
                  console.error('Video error details:', {
                    error: video.error,
                    networkState: video.networkState,
                    readyState: video.readyState
                  });
                  setError('Failed to load video. Please check the browser console for details.');
                }}
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  console.log('Video metadata loaded:', {
                    videoWidth: video.videoWidth,
                    videoHeight: video.videoHeight,
                    duration: video.duration,
                    readyState: video.readyState
                  });
                  // Force video to play
                  video.play().catch(err => {
                    console.error('Error playing video:', err);
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}
    </ContentLayout>
  );
};

export default Videos;

  